/**
 * Auth Headers Utility
 * Centralized token management for API requests
 * Ensures consistent Bearer token format across all requests
 */

/**
 * Creates Authorization header with Bearer token
 * @param {string} token - JWT token
 * @returns {Object} Headers object with Authorization
 * @throws {Error} If token is invalid
 */
export const createAuthHeader = (token) => {
  if (!token) {
    throw new Error("Token is required for authenticated requests");
  }

  if (!isValidTokenFormat(token)) {
    console.warn("Token format validation failed. Expected JWT with 3 parts.");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Gets token from localStorage and creates auth header
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaderFromStorage = () => {
  const token = sessionStorage.getItem("token");

  if (!token) {
    throw new Error("No token found in localStorage");
  }

  return createAuthHeader(token);
};

/**
 * Gets refresh token from localStorage and creates auth header
 * @returns {Object} Headers object with Authorization
 */
export const getRefreshAuthHeader = () => {
  const refreshToken = sessionStorage.getItem("refresh_token");

  if (!refreshToken) {
    throw new Error("No refresh token found in localStorage");
  }

  return createAuthHeader(refreshToken);
};

/**
 * Validates token format before sending
 * @param {string} token - JWT token to validate
 * @returns {boolean} True if token is valid format
 */
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== "string") {
    return false;
  }

  // Remove Bearer prefix if present
  const cleanToken = token.replace(/^Bearer\s+/i, "");

  // JWT tokens have 3 parts separated by dots
  const parts = cleanToken.split(".");
  if (parts.length !== 3) {
    return false;
  }

  // Each part should be base64 encoded (alphanumeric, +, /, -, _)
  const base64Regex = /^[A-Za-z0-9_-]+$/;
  return parts.every((part) => part.length > 0 && base64Regex.test(part));
};

/**
 * Creates complete headers object for API requests
 * @param {string} token - JWT token
 * @param {Object} additionalHeaders - Additional headers to merge
 * @returns {Object} Complete headers object
 * @throws {Error} If token format is invalid
 */
export const createRequestHeaders = (token, additionalHeaders = {}) => {
  if (!isValidTokenFormat(token)) {
    throw new Error("Invalid token format. Expected JWT token with 3 parts.");
  }

  return {
    ...createAuthHeader(token),
    ...additionalHeaders,
  };
};

/**
 * Extracts token from Bearer header string
 * @param {string} authHeader - Authorization header value
 * @returns {string|null} Extracted token or null
 */
export const extractBearerToken = (authHeader) => {
  if (!authHeader || typeof authHeader !== "string") {
    return null;
  }

  const bearerPrefix = "Bearer ";
  if (authHeader.startsWith(bearerPrefix)) {
    return authHeader.substring(bearerPrefix.length).trim();
  }

  // If no Bearer prefix, check if it's a valid token anyway
  return isValidTokenFormat(authHeader) ? authHeader : null;
};

/**
 * Clears all authentication tokens from storage
 * Call this on logout or authentication errors
 */
export const clearAuthTokens = async () => {
  const { performSignOut } = await import("./performSignOut");
  await performSignOut();
};
